import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { UserContext } from '@incentivegames/ig-frontend-common/lib/contexts/userContext';
import { IEvent, IEventParticipant, ITeamMember } from '@incentivegames/ig-types/lib/interfaces/apiContracts';
import { EventStatus } from '@incentivegames/ig-types/lib/enums/event/eventStatus';
import { communicationUtilities } from '@incentivegames/ig-frontend-common/lib/utilities/communicationUtilities';

import { getEntries, putTeamMember, setRevealAnimationInProgress } from 'store/actions/dreamteamActions';
import { IRootState } from 'store/serverInterfaces';
import { IParticipants } from 'interfaces/gameInterfaces';
import { sportUtilities } from 'utilities/sportUtilities';
import { dreamteamUtilities } from 'utilities/dreamteamUtilities';
import { dateTimeUtilities } from 'utilities/dateTimeUtilities';
import { ReactGACategories } from 'constants/appEnums';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface ICardContextProvider {
  children?: React.ReactElement;
  player: ITeamMember;
  receipt: string;
  gameId: number;
  noEntry: boolean;
  stubbedMode?: boolean;
  stubbedModeData?: any;
  zIndex?: number;
  decisionTimeBonus?: boolean;
  newSigningBonus?: boolean;
}

export interface ICardContext {
  noEntry: boolean;
  revealed: boolean;
  player: ITeamMember;
  teamName: string;
  teamCode: string;
  handleOnClick: () => void;
  shirtImage: string;
  currentTime: number;
  formattedMatchStartTime: string;
  countDownText: string;
  isFullTime: boolean;
  matchHasStarted: boolean;
  hasExpired: boolean;
  canBeRevealed: boolean;
  showPlayer: boolean;
  showPoints: boolean;
  eventHomeScore?: number;
  eventAwayScore?: number;
  eventActive?: boolean;
  triggerRevealAnimation: boolean;
  onRevealFinishCallback: () => void;
  zIndex: number;
  loading: boolean;
  stubbedMode: boolean;
  points: number;
  shirtNumber: string;
  position: string;
  eventParticipants: IParticipants[];
  participantCodeOne: string | undefined;
  participantCodeTwo: string | undefined;
  revealTime: number;
  decisionTimeBonus: boolean;
  newSigningBonus: boolean;
}

export const CardContext = createContext<ICardContext>({} as ICardContext);

export const CardContextProvider = (contextProps: ICardContextProvider) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const sportsReducer = useSelector((state: IRootState) => state.SportsReducer);
  const [loading, setLoading] = useState<boolean>(false);
  const [revealed, setRevealed] = useState<boolean>(contextProps.player?.revealed);
  const [event, setEvent] = useState<IEvent | undefined>();
  const [triggerRevealAnimation, setTriggerRevealAnimation] = useState<boolean>(false);
  const [shirtImage, setShirtImage] = useState<string>('');

  const userContext = useContext(UserContext);

  const teamName =
    dreamteamUtilities.getTeamName(
      contextProps.player?.eventParticipants?.find((team: IEventParticipant) => {
        return team.participantCode === contextProps.player.teamCode;
      })?.participantName
    ) ||
    dreamteamUtilities.getTeamName(contextProps.player.teamCode) ||
    '';
  const currentTime = dayjs().valueOf();
  const hasExpired = contextProps.player?.expiryTime <= currentTime || (contextProps.noEntry && !contextProps.stubbedMode);
  const canBeRevealed = !revealed && !hasExpired && contextProps.player?.revealTime !== undefined && currentTime >= contextProps.player?.revealTime;
  const showPlayer = canBeRevealed || revealed;
  const matchHasStarted = event ? currentTime >= event.eventStartTime : currentTime >= contextProps.player.eventStartTime;
  const isFullTime: boolean = (() => {
    if (contextProps.stubbedMode) return contextProps.stubbedModeData.eventPeriod === EventStatus.fullTime;
    return event ? event.eventPeriod === EventStatus.fullTime : false;
  })();
  const eventActive = contextProps.stubbedMode ? contextProps.stubbedModeData.eventActive : event?.eventActive;
  const eventHomeScore = contextProps.stubbedMode ? contextProps.stubbedModeData.eventHomeScore : event?.eventHomeScore;
  const eventAwayScore = contextProps.stubbedMode ? contextProps.stubbedModeData.eventAwayScore : event?.eventAwayScore;

  useEffect(() => {
    let handleSetShirtImage: React.Dispatch<React.SetStateAction<string>> | null = setShirtImage;
    if (contextProps.player?.teamCode) {
      setEvent(sportsReducer.events?.find((event: IEvent) => event.eventId === contextProps.player.eventId));
    }

    if (contextProps.player?.teamCode) {
      sportUtilities.getShirtImage(contextProps.player?.teamCode, (path) => handleSetShirtImage?.(path), false, event?.compCode);
    }

    return () => {
      handleSetShirtImage = null;
    };
  }, [setShirtImage, contextProps.player, event, sportsReducer.events]);

  const formatMatchStartTime = () => {
    if (!event && !contextProps.player.eventStartTime) return '';
    const regionTimeZoneMap: { [key: string]: string } = {
      bg: 'Europe/Sofia',
      ie: 'Europe/Dublin',
      ng: 'Africa/Lagos',
      nl: 'Europe/Amsterdam',
      uk: 'Europe/London'
    };
    let eventStartTime = dayjs(contextProps.stubbedMode ? contextProps.player.eventStartTime : event?.eventStartTime);
    const timeZone = regionTimeZoneMap[process.env.REACT_APP_REGION || 'uk'];

    if (timeZone) {
      eventStartTime = eventStartTime.tz(timeZone);
    }

    if (!eventStartTime) return '';

    if (event && !event.eventActive) return t('tbc');

    const dateFormat = parseInt(eventStartTime.format('D'));
    const getOrdinalSuffix = dateTimeUtilities.getNthDay(dateFormat);

    return `${t(eventStartTime.format('ddd').toLowerCase())} ${eventStartTime.format('D')}${t(getOrdinalSuffix)}${eventStartTime.format(', H')}${eventStartTime.format(':mm')}`;
  };

  const handleOnClick = () => {
    // TODO refactor this beast into something smaller
    if (!dreamteamUtilities.redirectIfNotAuthenticated(userContext.user.isAuthenticated, dispatch)) {
      if (canBeRevealed && !contextProps.player.revealed) {
        if (contextProps.player.expiryTime > new Date().getTime()) {
          if (!contextProps.stubbedMode) {
            dispatch(
              putTeamMember(userContext.user.token!, contextProps.receipt, contextProps.player?.selnNumber, () => {
                setTriggerRevealAnimation(true);
                dispatch(setRevealAnimationInProgress(true));
                setLoading(false);
                try {
                  communicationUtilities.sendPostMessage.tracking('player-revealed');
                } catch (error) {
                  dreamteamUtilities.recordEventGA(ReactGACategories.error, 'Failure', `On sending postMessage tracking player-revealed -> ' ${error}`);
                }
              })
            );
            setLoading(true);
          }
        } else {
          dispatch(getEntries(userContext.user.token!, contextProps.gameId));
        }
      } else if (contextProps.player.revealed && !eventActive) {
        communicationUtilities.sendPostMessage.howToPlay();
      }
    }

    if (contextProps.stubbedMode) {
      setTriggerRevealAnimation(true);
    }
  };
  const renderCountDownText = useCallback(() => {
    if (!contextProps.stubbedMode && (!event || !event.eventPeriod)) return '';

    let period = contextProps.stubbedModeData?.eventPeriod?.toUpperCase();
    let eventClockTime = contextProps.stubbedModeData?.eventClockTime;

    if (event) {
      period = event.eventPeriod.toUpperCase();
      eventClockTime = event.eventClockTime;
    }

    switch (period) {
      case EventStatus.preMatch:
        return '';
      case EventStatus.firstHalf:
      case EventStatus.secondHalf:
        return eventClockTime && parseInt(eventClockTime) > 0 ? eventClockTime + "'" : '';
      case EventStatus.halfTime:
        return t('ht');
      case EventStatus.fullTime:
        return t('ft');
      default:
        return '';
    }
  }, [contextProps, event, t]);

  const onRevealFinishCallback = () => {
    setRevealed(true);

    // This dispatch triggers an edge case after the reveal animation -
    // - is complete the scroll position will be reset, I was unable to -
    // - find the cause.
    dispatch(setRevealAnimationInProgress(false));
  };

  const getParticipantCode = (participantIndex: number) =>
    contextProps.player.eventParticipants ? dreamteamUtilities.getTeamCode(contextProps.player.eventParticipants[participantIndex].participantCode) : undefined;

  const value: ICardContext = {
    noEntry: contextProps.noEntry,
    revealed: revealed,
    player: contextProps.player,
    teamName,
    teamCode: contextProps.player.teamCode,
    handleOnClick: handleOnClick,
    shirtImage,
    currentTime,
    formattedMatchStartTime: formatMatchStartTime(),
    isFullTime,
    hasExpired,
    matchHasStarted,
    canBeRevealed,
    showPlayer,
    showPoints: contextProps.player?.points > 0 && revealed,
    points: contextProps.player.points,
    eventHomeScore,
    eventAwayScore,
    eventActive,
    triggerRevealAnimation,
    onRevealFinishCallback,
    zIndex: contextProps.zIndex || 1,
    loading,
    stubbedMode: contextProps.stubbedMode || false,
    shirtNumber: contextProps.player.shirtNumber,
    position: contextProps.player.position,
    eventParticipants: contextProps.player.eventParticipants,
    participantCodeOne: getParticipantCode(0),
    participantCodeTwo: getParticipantCode(1),
    countDownText: renderCountDownText(),
    revealTime: contextProps.player.revealTime,
    decisionTimeBonus: contextProps.decisionTimeBonus || false,
    newSigningBonus: contextProps.newSigningBonus || false
  };

  return <CardContext.Provider value={value}>{contextProps.children}</CardContext.Provider>;
};
